import EmblaCarousel from 'embla-carousel';
import Autoplay from 'embla-carousel-autoplay';
import {
  addPrevNextBtnsClickHandlers,
  addTogglePrevNextBtnsActive,
  addDotBtnsAndClickHandlers,
  addThumbBtnsClickHandlers,
  addToggleThumbBtnsActive,
} from './embla-extras';

class EmblaComponent extends HTMLElement {
  constructor() {
    super();
    this.emblaApi;
    this.emblaApiThumbs;
    this.emblaNode = this.querySelector('.embla');
    this.emblaThumbs = this.querySelector('.embla-thumbs');
    this.viewportNode = this.querySelector('.embla__viewport');
    this.viewportNodeThumbs = this.querySelector('.embla-thumbs__viewport');
    this.prevBtn = this.querySelector('.embla__button--prev');
    this.nextBtn = this.querySelector('.embla__button--next');
    this.dotsNode = this.querySelector('.embla__dots');
    this.progressBar = this.querySelector('.embla__progress__bar');
    this.options = this.returnConfig();
    this.optionsThumbs = this.returnThumbsConfig();
    this.activeSlide = this.querySelector('.embla__slide');

    document.addEventListener('DOMContentLoaded', (e) => {
      this.extraSlideEls = this.querySelector('embla-extra-slide-els');
      this.initEmbla();
      this.emblaApi.on('select', () => this.handleSelect());
      this.handleActiveSlide();
    });
  }

  initEmbla() {
    this.emblaApi = EmblaCarousel(
      this.viewportNode,
      this.options,
      this.emblaNode.getAttribute('data-autoplay') === 'true'
        ? [Autoplay(this.returnAutoplayConfig())]
        : null,
    );

    if (this.emblaApi) {
      this.emblaApi.on('select', () => this.handleSelect());
      this.handleSelect(); // Ensure the correct slide is active on load
      this.addSlideClickHandlers(); // Add click event to slides
    }

    if (this.viewportNodeThumbs) {
      this.emblaApiThumbs = EmblaCarousel(this.viewportNodeThumbs, this.optionsThumbs);

      const prevThumbsBtn = this.querySelector('.embla-thumbs__button--prev');
      const nextThumbsBtn = this.querySelector('.embla-thumbs__button--next');

      if (prevThumbsBtn && nextThumbsBtn) {
        prevThumbsBtn.addEventListener('click', () => this.emblaApiThumbs.scrollPrev());
        nextThumbsBtn.addEventListener('click', () => this.emblaApiThumbs.scrollNext());
      }
    }

    if (this.prevBtn && this.nextBtn) {
      addPrevNextBtnsClickHandlers(this.emblaApi, this.prevBtn, this.nextBtn);
      addTogglePrevNextBtnsActive(this.emblaApi, this.prevBtn, this.nextBtn);
    }

    if (this.dotsNode) {
      addDotBtnsAndClickHandlers(this.emblaApi, this.dotsNode);
    }

    if (this.emblaApiThumbs) {
      addThumbBtnsClickHandlers(this.emblaApi, this.emblaApiThumbs);
      addToggleThumbBtnsActive(this.emblaApi, this.emblaApiThumbs);
    }
  }

  returnConfig() {
    return {
      align: this.emblaNode.getAttribute('data-align') || 'center',
      loop: this.emblaNode.getAttribute('data-loop') === 'true' ? true : false,
      slidesToScroll: +this.emblaNode.getAttribute('data-slides-to-scroll') || 'auto',
      startIndex: +this.emblaNode.getAttribute('data-start-index') || 0,
      containScroll: 'trimSnaps',
      axis: this.emblaNode.getAttribute('data-axis') || 'x',
      duration: +this.emblaNode.getAttribute('data-duration') || 25,
      inViewThreshold: 1,
    };
  }

  returnAutoplayConfig() {
    return {
      delay: +this.emblaNode.getAttribute('data-autoplay-speed') || 4000,
    };
  }

  returnThumbsConfig() {
    return {
      containScroll: 'keepSnaps',
      dragFree: true,
      loop: this.emblaThumbs?.getAttribute('data-thumbs-loop') === 'true' ? true : false,
    };
  }

  handleSelect() {
    if (!this.emblaApi) return;

    const activeIndex = this.emblaApi.selectedScrollSnap();
    const slides = this.querySelectorAll('.embla__slide');
    const videos = this.querySelectorAll('.embla__slide video');

    // Remove active class from all slides
    slides.forEach((slide) => slide.classList.remove('embla__slide--active'));

    // Pause all videos
    videos.forEach((video) => {
      video.pause();
      video.currentTime = 0; // Reset video to start
    });

    // Find and activate the new slide
    const newActiveSlide = slides[activeIndex];
    if (newActiveSlide) {
      newActiveSlide.classList.add('embla__slide--active');

      // Play the video inside the active slide
      const videoInActiveSlide = newActiveSlide.querySelector('video');
      if (videoInActiveSlide) {
        videoInActiveSlide.play();
      }
    }

    if (this.progressBar) {
      const totalSlides = this.emblaApi.scrollSnapList().length;
      const progressPercentage = ((activeIndex + 1) / totalSlides) * 100;

      this.progressBar.style.width = `${progressPercentage}%`;
    }
  }

  addSlideClickHandlers() {
    const slides = this.querySelectorAll('.embla__slide');

    slides.forEach((slide, index) => {
      slide.addEventListener('click', () => {
        if (this.emblaApi) {
          this.emblaApi.scrollTo(index); // Scroll to clicked slide
          this.handleSelect(); // Manually trigger handleSelect() to update active state
        }
      });
    });
  }

  handleActiveSlide(newSlide) {
    if (newSlide) {
      this.activeSlide.classList.remove('embla__slide--active');
      this.activeSlide = newSlide;
      this.activeSlide.classList.add('embla__slide--active');
    } else {
      this.activeSlide.classList.add('embla__slide--active');
    }
  }
}

customElements.define('embla-component', EmblaComponent);

/* functionality for elements that are not part of slides/thumbnails but still need to get toggled when slides change */

class EmblaExtraSlideEls extends HTMLElement {
  constructor() {
    super();
    this.extraSlideElements = this.querySelectorAll('.embla__extra-slide-el');
    this.showExtraSlideEl('0');
  }

  showExtraSlideEl(index) {
    if (!this.extraSlideElements) return;
    this.extraSlideElements.forEach((extraEl) => {
      if (extraEl.getAttribute('data-extra-slide-el-index') !== index) {
        extraEl.classList.remove('is-visible');
      } else {
        extraEl.classList.add('is-visible');
      }
    });
  }
}

customElements.define('embla-extra-slide-els', EmblaExtraSlideEls);
